import React, { HTMLAttributes, HTMLProps } from 'react';

function assignProps<T extends HTMLProps<HTMLElement>>(
  el: HTMLElement,
  { style, ...props }: T,
) {
  if (style) {
    Object.entries(style).forEach(([name, value]) => {
      // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      (el as any).style[name] = value;
    });
  }
  Object.assign(el, props);
}

/**
 * El hook useDomState permite aplicar propiedades a un
 * elemento del DOM de la forma más eficiente posible.
 *
 * Este hook no permite el uso de sx ya que por las características
 * del sx, el re-renderizado es necesario.
 *
 * @example
 *
 *
 * const TestComponent = () => {
  const { domProps, setDomProps } = useDomState<BoxProps>({
    style: {
      position: 'fixed',
      left: 0,
      top: 0,
      width: '150px',
      height: '150px',
      backgroundColor: '#ccc',
    },
  });
  const initialPosition = React.useRef({ boxX: 0, boxY: 0, x: 0, y: 0 });
  const isDragging = React.useRef(false);
  const [state, setState] = React.useState(1);

  React.useEffect(() => {
    const move = (ev: MouseEvent) => {
      if (isDragging.current) {
        const moveX = ev.clientX - initialPosition.current.x;
        const moveY = ev.clientY - initialPosition.current.y;
        const newLeft = initialPosition.current.boxX + moveX;
        const newTop = initialPosition.current.boxY + moveY;
        setDomProps({
          style: {
            left: `${newLeft}px`,
            top: `${newTop}px`,
          },
        });
      }
    };

    const removeSuscription = () => {
      isDragging.current = false;
    };

    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', removeSuscription);

    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', removeSuscription);
    };
  }, [setDomProps]);

  return (
    <Box
      {...domProps}
      onMouseDown={(ev) => {
        initialPosition.current = {
          boxX: Number.parseInt(domProps.style?.left as string, 10),
          boxY: Number.parseInt(domProps.style?.top as string, 10),
          x: ev.clientX,
          y: ev.clientY,
        };
        isDragging.current = true;
      }}
    >
      Esta caja es arrastrable. Renderizados: {state}
      <Button onClick={() => setState((current) => current + 1)}>
        Renderizar
      </Button>
    </Box>
  );
};
 */
export function useDomState<T extends HTMLAttributes<HTMLElement>>(
  initialDomProps?: T,
) {
  const domProps = React.useRef<T & { ref: (el: HTMLElement) => void }>({
    ...(initialDomProps ?? ({} as T)),
    ref: React.useCallback((el: HTMLElement) => {
      ref.current = el;
      assignProps(ref.current, domProps.current);
    }, []),
  });
  const ref = React.useRef<HTMLElement | null>(null);
  return {
    domProps: domProps.current,
    setDomProps: React.useCallback(({ style, ...newDomProps }: Partial<T>) => {
      if (style) {
        const newStyles = { ...domProps.current.style, ...style };
        domProps.current.style = newStyles;
      }
      Object.assign(domProps.current, newDomProps);
      if (ref.current) assignProps(ref.current, domProps.current);
    }, []),
  };
}
